import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CaretRightOutlined,
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
  SnippetsOutlined,
  VerticalAlignBottomOutlined,
  VerticalAlignTopOutlined,
} from "@ant-design/icons";
import { LayerType, modTypes } from "@blings/blings-player";
import { motionAPI } from "@blings/blings-player/editor-tools";
import { useMst } from "../../stores/main";
import {
  prefillLayerFromInspector,
  resolveLayerDetails,
  resolveLayerFromSelection,
} from "../../helpers/layerInspectorPrefill";
import { modsDefinitions } from "../mods/mod-definitions/mod-definitions";
import { isDisabledModButton } from "../mods/NewModButtons/NewModButtonsList";
import { ConnectorIcon } from "../../icons-and-animations/Icons";
import {
  isColorLayerSelectable,
  isParagraphLayerSelectable,
  isTextLayerSelectable,
} from "../../helpers/layerSelectorFilters";
import type { LayerInspectorSelection } from "../../helpers/layerInspectorTools";
import { meetsPlayerVersion } from "../../helpers/playerVersion";
import { useLayerStructuralActions } from "./useLayerStructuralActions";
import type { LayerSortAction } from "./useLayerStructuralActions";
import { writeLayerClipboardText } from "../../hooks/useLayerClipboardShortcuts";
import { formatPrimaryShortcut } from "../../utils/shortcutKeys";
import {
  formatTextAnimationPresetName,
  TEXT_ANIMATION_PHASES,
} from "../TimelinePanel/utils/keyframeHelpers";
import type { TextAnimationPhase } from "../TimelinePanel/utils/keyframeHelpers";
import {
  MotionPresetGallery,
  buildMotionPresetGalleryItems,
} from "../TimelinePanel/MotionPresetGallery";
import type { MotionPresetSelectHandler } from "../TimelinePanel/MotionPresetGallery";
import { buildTimelinePresetTiming } from "../TimelinePanel/utils/animationPresetHelpers";
import { normalizeAssetId } from "../TimelinePanel/utils/timingModHelpers";
import { getResolvedLayerTiming } from "../TimelinePanel/TimelineTrack";
import { useAnimationJsonEdit } from "../TimelinePanel/hooks/useAnimationJsonEdit";
import {
  buildTextAnimationSelectorTiming,
  supportsTimelineAnimation,
  TEXT_ANIMATION_PHASE_LABELS,
} from "../TimelinePanel/utils/timelineLayerAnimationUtils";

type ConnectorAvailabilityContext = {
  selection?: LayerInspectorSelection;
  jsonFile?: any;
  fallbackAssetKey: string;
};

type ConnectorEntry = {
  key: string;
  label: string;
  type: modTypes;
  customAvailability?: (ctx: ConnectorAvailabilityContext) => boolean;
};

const buildSelectorArgs = (ctx: ConnectorAvailabilityContext) => {
  if (!ctx.selection?.layerName || !ctx.jsonFile) return null;

  const resolved = resolveLayerDetails(
    ctx.selection,
    ctx.jsonFile,
    ctx.fallbackAssetKey
  );
  if (!resolved) return null;
  const { layerName, layerUid, assetId } = resolved;

  return {
    assetId,
    layerName,
    layerUid,
    json: ctx.jsonFile,
    fallbackAssetId: ctx.fallbackAssetKey,
  };
};

const PRIMARY_CONNECTORS: ConnectorEntry[] = [
  {
    key: "text",
    label: "Text",
    type: modTypes.text,
    customAvailability: (ctx) => {
      const filterArgs = buildSelectorArgs(ctx);
      if (!filterArgs) return false;
      return isTextLayerSelectable(filterArgs);
    },
  },
  { key: "image", label: "Image", type: modTypes.asset },
  {
    key: "color",
    label: "Color",
    type: modTypes.themeColor,
    customAvailability: (ctx) => {
      const filterArgs = buildSelectorArgs(ctx);
      if (!filterArgs) return false;
      return isColorLayerSelectable(filterArgs);
    },
  },
  { key: "hide", label: "Hide", type: modTypes.hideLayer },
];

const INTERACTIVE_CONNECTORS: ConnectorEntry[] = [
  { key: "open-link", label: "Open link", type: modTypes.interactiveOpen },
  { key: "location", label: "Location", type: modTypes.interactiveLocation },
  { key: "calendar", label: "Calendar", type: modTypes.interactiveCalendar },
  {
    key: "branch-timeline",
    label: "Branch timeline",
    type: modTypes.interactiveConcat,
  },
  {
    key: "input",
    label: "Input",
    type: modTypes.interactiveInput,
    customAvailability: (ctx) => {
      const filterArgs = buildSelectorArgs(ctx);
      if (!filterArgs) return false;
      return isParagraphLayerSelectable(filterArgs);
    },
  },
  {
    key: "submit-form",
    label: "Submit form",
    type: modTypes.interactiveForm,
  },
];

const MENU_WIDTH = 256;
const MENU_HEIGHT_ESTIMATE = 360;
const MENU_MARGIN = 16;

type MenuSize = {
  width: number;
  height: number;
};

type FlyoutItemProps = {
  label: string;
  flyoutRef: React.RefObject<HTMLDivElement>;
  icon?: ReactNode;
  children: ReactNode;
};

type MenuActionItem = {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
};

const LAYER_SORT_ACTIONS: Array<{
  action: LayerSortAction;
  icon: ReactNode;
  label: string;
  shortcutKey: string;
  shift?: boolean;
}> = [
  {
    action: "bringToFront",
    icon: <VerticalAlignTopOutlined />,
    label: "Bring to front",
    shortcutKey: "]",
    shift: true,
  },
  {
    action: "bringForward",
    icon: <ArrowUpOutlined />,
    label: "Bring forward",
    shortcutKey: "]",
  },
  {
    action: "sendBackward",
    icon: <ArrowDownOutlined />,
    label: "Send backward",
    shortcutKey: "[",
  },
  {
    action: "sendToBack",
    icon: <VerticalAlignBottomOutlined />,
    label: "Send to back",
    shortcutKey: "[",
    shift: true,
  },
];

const getViewportAwarePosition = (
  clientX: number,
  clientY: number,
  menuSize: MenuSize
) => {
  if (typeof window === "undefined") {
    return { top: clientY, left: clientX };
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const maxMenuHeight = viewportHeight - MENU_MARGIN * 2;
  const menuHeight = Math.min(menuSize.height, maxMenuHeight);
  const menuWidth = menuSize.width;
  const availableBelow = viewportHeight - clientY - MENU_MARGIN;
  const availableAbove = clientY - MENU_MARGIN;

  let top = clientY;
  if (menuHeight > availableBelow && availableAbove > availableBelow) {
    top = clientY - menuHeight;
  }

  return {
    top: Math.max(
      MENU_MARGIN,
      Math.min(top, viewportHeight - menuHeight - MENU_MARGIN)
    ),
    left: Math.max(
      MENU_MARGIN,
      Math.min(clientX, viewportWidth - menuWidth - MENU_MARGIN)
    ),
  };
};

export const LayerConnectorMenu = observer(() => {
  const {
    playerStore,
    modsStore,
    platformStore: { projectWorkspaceVersion, selectedProjectBasicInfo },
  } = useMst();
  const {
    layerInspectorContextMenu,
    hideLayerInspectorContextMenu,
    jsonFile,
    mainAssetKey,
    layerInspectorEnabled,
    layerInspectorMode,
  } = playerStore;
  const {
    copyLayers,
    deleteLayers,
    duplicateLayers,
    sortLayers,
    isApplyingLayerAction,
  } = useLayerStructuralActions();
  const { applyAnimationJsonEdit } = useAnimationJsonEdit();

  const menuRef = useRef<HTMLDivElement>(null);
  const animationFlyoutRef = useRef<HTMLDivElement>(null);
  const textAnimationFlyoutRef = useRef<HTMLDivElement>(null);
  const [menuSize, setMenuSize] = useState<MenuSize>({
    width: MENU_WIDTH,
    height: MENU_HEIGHT_ESTIMATE,
  });
  const [animationFlyoutPlacement, setAnimationFlyoutPlacement] = useState<
    "down" | "up"
  >("down");

  useEffect(() => {
    if (!layerInspectorContextMenu) return;
    const handleClick = (event: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) {
        hideLayerInspectorContextMenu();
      }
    };
    const handleContextMenu = (event: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) {
        hideLayerInspectorContextMenu();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        hideLayerInspectorContextMenu();
      }
    };
    document.addEventListener("mousedown", handleClick, true);
    document.addEventListener("contextmenu", handleContextMenu, true);
    document.addEventListener("keyup", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClick, true);
      document.removeEventListener("contextmenu", handleContextMenu, true);
      document.removeEventListener("keyup", handleEscape);
    };
  }, [hideLayerInspectorContextMenu, layerInspectorContextMenu]);

  useEffect(() => {
    if (!layerInspectorContextMenu) return;
    if (!layerInspectorEnabled) {
      hideLayerInspectorContextMenu();
    }
  }, [
    hideLayerInspectorContextMenu,
    layerInspectorContextMenu,
    layerInspectorEnabled,
  ]);

  useLayoutEffect(() => {
    if (!layerInspectorContextMenu || !menuRef.current) return;

    const updateMenuSize = () => {
      if (!menuRef.current) return;
      const rect = menuRef.current.getBoundingClientRect();
      setMenuSize((currentSize) => {
        const nextSize = {
          width: Math.ceil(rect.width),
          height: Math.ceil(rect.height),
        };
        if (
          currentSize.width === nextSize.width &&
          currentSize.height === nextSize.height
        ) {
          return currentSize;
        }
        return nextSize;
      });
    };

    updateMenuSize();
    window.addEventListener("resize", updateMenuSize);
    return () => {
      window.removeEventListener("resize", updateMenuSize);
    };
  }, [layerInspectorContextMenu]);

  const anchorStyle = useMemo(() => {
    if (!layerInspectorContextMenu) return undefined;
    const { clientX, clientY } = layerInspectorContextMenu.position;

    return getViewportAwarePosition(clientX, clientY, menuSize);
  }, [layerInspectorContextMenu, menuSize]);
  const motionPresetItems = useMemo(
    () => buildMotionPresetGalleryItems(motionAPI.presets.listPresets()),
    []
  );
  const textPresetNames = useMemo(
    () => motionAPI.textPresets.listTextPresets(),
    []
  );
  const updateAnimationFlyoutPlacement = (
    triggerElement: HTMLElement,
    flyoutElement?: HTMLElement | null
  ) => {
    if (typeof window === "undefined") {
      return;
    }

    const triggerRect = triggerElement.getBoundingClientRect();
    const flyoutHeight = flyoutElement?.getBoundingClientRect().height ?? 520;
    const availableBelow = window.innerHeight - triggerRect.top - MENU_MARGIN;
    const availableAbove = triggerRect.bottom - MENU_MARGIN;

    setAnimationFlyoutPlacement(
      flyoutHeight > availableBelow && availableAbove > availableBelow
        ? "up"
        : "down"
    );
  };

  const renderFlyoutItem = ({
    label,
    flyoutRef,
    icon = <PlusOutlined />,
    children,
  }: FlyoutItemProps) => (
    <div
      className="layer-connector-menu__flyout-item"
      onMouseEnter={(event) =>
        updateAnimationFlyoutPlacement(event.currentTarget, flyoutRef.current)
      }
      onFocus={(event) =>
        updateAnimationFlyoutPlacement(event.currentTarget, flyoutRef.current)
      }
    >
      <button className="layer-connector-menu__item" type="button">
        <span className="layer-connector-menu__item-icon">{icon}</span>
        <span>{label}</span>
        <span className="layer-connector-menu__item-caret">
          <CaretRightOutlined />
        </span>
      </button>
      <div
        className={`layer-connector-menu__animation-flyout layer-connector-menu__animation-flyout--${animationFlyoutPlacement}`}
        ref={flyoutRef}
      >
        {children}
      </div>
    </div>
  );

  if (!layerInspectorContextMenu || !anchorStyle) {
    return null;
  }

  const selection = layerInspectorContextMenu.layer;
  const selectionList = layerInspectorContextMenu.selections?.length
    ? layerInspectorContextMenu.selections
    : selection
    ? [selection]
    : [];
  const primarySelection = selection ?? selectionList[selectionList.length - 1];
  const availabilityArgs = {
    selection: primarySelection,
    jsonFile,
    fallbackAssetKey: mainAssetKey,
  };
  const resolvedSelection =
    primarySelection && jsonFile
      ? resolveLayerDetails(primarySelection, jsonFile, mainAssetKey)
      : null;
  const resolvedLayer =
    primarySelection && jsonFile
      ? resolveLayerFromSelection(primarySelection, jsonFile)
      : null;
  const isVideoLayer = resolvedSelection?.layerType === LayerType.video;
  const isTextLayer = resolvedSelection?.layerType === LayerType.text;
  const canAddTimelineAnimation = Boolean(
    resolvedSelection && supportsTimelineAnimation(resolvedSelection.layerType)
  );

  const meetsVersionRequirement = (type: modTypes) => {
    const def = modsDefinitions[type];
    return meetsPlayerVersion(
      selectedProjectBasicInfo?.playerVersionToUse,
      def?.fromVersion
    );
  };

  const canPrefillConnector = (type: modTypes): boolean => {
    if (!primarySelection || !jsonFile) return false;
    const snapshot: Record<string, unknown> = { type };
    prefillLayerFromInspector({
      snapshot,
      isAssetChange: type === modTypes.asset,
      selection: primarySelection,
      selections: selectionList,
      jsonFile,
      fallbackAssetId: mainAssetKey,
    });
    if (type === modTypes.asset) {
      return Boolean(snapshot.imageName);
    }
    return Boolean(snapshot.layerName);
  };

  const getAvailability = (entry: ConnectorEntry): boolean => {
    if (isVideoLayer && entry.type !== modTypes.asset) {
      return false;
    }
    const def = modsDefinitions[entry.type];
    if (!def) return false;
    if (
      isDisabledModButton(playerStore, def.disabled, projectWorkspaceVersion)
    ) {
      return false;
    }
    if (!meetsVersionRequirement(entry.type)) return false;

    if (entry.customAvailability) {
      return entry.customAvailability(availabilityArgs);
    }

    return canPrefillConnector(entry.type);
  };

  const handleAddMod = (type: modTypes) => {
    const def = modsDefinitions[type];
    if (!def) return;
    modsStore.addMod(def.change, undefined, selectionList);
    hideLayerInspectorContextMenu();
  };

  const getMotionContext = () => {
    if (!jsonFile || !resolvedSelection || !resolvedLayer) {
      return null;
    }

    const layer = resolvedLayer.layer as {
      ip?: number;
      op?: number;
      st?: number;
      nm?: string;
      uid?: number;
    };
    const ip = typeof layer.ip === "number" ? layer.ip : 0;
    const op =
      typeof layer.op === "number"
        ? layer.op
        : typeof (jsonFile as { op?: number }).op === "number"
        ? (jsonFile as { op: number }).op
        : ip + 30;
    const layerTiming = getResolvedLayerTiming(
      {
        ...layer,
        ip,
        op,
        st: typeof layer.st === "number" ? layer.st : 0,
        nm: resolvedSelection.layerName,
        uid: resolvedSelection.layerUid,
        assetId: resolvedSelection.assetId,
        timelineOffset: 0,
      } as any,
      modsStore.mods?.slice() ?? []
    );
    const motionTarget = {
      assetId: normalizeAssetId(resolvedSelection.assetId),
      layerName: resolvedSelection.layerName,
      layerUid: resolvedSelection.layerUid,
    };

    return { layerTiming, motionTarget };
  };

  const handleDuplicateLayers = () => {
    if (!selectionList.length) return;
    duplicateLayers({
      selections: selectionList,
      primarySelection,
    });
    hideLayerInspectorContextMenu();
  };

  const handleDeleteLayers = () => {
    if (!selectionList.length) return;
    deleteLayers({ selections: selectionList });
    hideLayerInspectorContextMenu();
  };

  const handleCopyLayers = () => {
    if (!selectionList.length) return;

    void (async () => {
      const clipboardText = await copyLayers({ selections: selectionList });
      if (clipboardText) {
        await writeLayerClipboardText(clipboardText);
      }
      hideLayerInspectorContextMenu();
    })();
  };

  const handleSortLayers = (action: LayerSortAction) => {
    if (!selectionList.length) return;
    sortLayers(action, { selections: selectionList });
    hideLayerInspectorContextMenu();
  };

  const handleAddLayerPreset: MotionPresetSelectHandler = (
    presetName,
    phase,
    direction
  ) => {
    const motionContext = getMotionContext();
    if (!motionContext) {
      return;
    }
    const { layerTiming, motionTarget } = motionContext;

    const didApply = applyAnimationJsonEdit((workingAnimation) => {
      const timing = buildTimelinePresetTiming({
        phase,
        ip: layerTiming.ip,
        op: layerTiming.op,
        timelineOffset: layerTiming.timelineOffset,
        frameRate: (workingAnimation as { fr?: number }).fr,
      });
      const applied = motionAPI.presets.applyPreset(
        workingAnimation as any,
        motionTarget,
        presetName as Parameters<typeof motionAPI.presets.applyPreset>[2],
        {
          timing,
          phase,
          ...(direction ? { direction } : {}),
        }
      );

      if (!applied.changes.length) {
        return false;
      }

      return applied;
    });

    if (didApply !== false) {
      hideLayerInspectorContextMenu();
    }
  };

  const handleAddTextPreset = (
    presetName: string,
    phase: TextAnimationPhase
  ) => {
    const motionContext = getMotionContext();
    if (!motionContext) {
      return;
    }
    const { layerTiming, motionTarget } = motionContext;
    const timing = buildTextAnimationSelectorTiming({
      phase,
      existingTiming: {},
      layerTiming,
    });

    const didApply = applyAnimationJsonEdit((workingAnimation) => {
      const applied = motionAPI.textPresets.applyTextPreset(
        workingAnimation as any,
        motionTarget,
        presetName as Parameters<
          typeof motionAPI.textPresets.applyTextPreset
        >[2],
        {
          timing,
          phase,
          grouping: "characters",
          staggerFrames: 2,
        }
      );

      if (!applied.changes.length) {
        return false;
      }

      return applied;
    });

    if (didApply !== false) {
      hideLayerInspectorContextMenu();
    }
  };

  const renderAnimationSection = () => {
    const canAddCharacterAnimation = isTextLayer && textPresetNames.length > 0;
    if (
      !canAddTimelineAnimation ||
      (!motionPresetItems.length && !canAddCharacterAnimation)
    ) {
      return null;
    }

    return (
      <>
        <div className="layer-connector-menu__section-title">Animation</div>
        {motionPresetItems.length
          ? renderFlyoutItem({
              label: "Add animation",
              flyoutRef: animationFlyoutRef,
              children: (
                <MotionPresetGallery
                  items={motionPresetItems}
                  onSelect={handleAddLayerPreset}
                />
              ),
            })
          : null}
        {canAddCharacterAnimation
          ? renderFlyoutItem({
              label: "Character animation",
              flyoutRef: textAnimationFlyoutRef,
              children: (
                <div
                  className="layer-connector-menu__text-preset-flyout"
                  role="menu"
                  aria-label="Character animation presets"
                >
                  {textPresetNames.map((presetName) => (
                    <div
                      className="layer-connector-menu__text-preset-row"
                      key={presetName}
                    >
                      <span className="layer-connector-menu__text-preset-name">
                        {formatTextAnimationPresetName(presetName)}
                      </span>
                      <span className="layer-connector-menu__text-preset-phases">
                        {TEXT_ANIMATION_PHASES.map((phase) => (
                          <button
                            className="layer-connector-menu__text-preset-phase"
                            key={phase}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleAddTextPreset(presetName, phase);
                            }}
                            type="button"
                          >
                            {TEXT_ANIMATION_PHASE_LABELS[phase]}
                          </button>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              ),
            })
          : null}
      </>
    );
  };

  const renderMenuAction = (item: MenuActionItem) => (
    <button
      key={item.key}
      className={`layer-connector-menu__item${
        item.danger ? " layer-connector-menu__item--danger" : ""
      }${item.disabled ? " layer-connector-menu__item--disabled" : ""}`}
      disabled={item.disabled}
      onClick={item.onClick}
      type="button"
    >
      <span className="layer-connector-menu__item-icon">{item.icon}</span>
      <span className="layer-connector-menu__item-label">{item.label}</span>
      {item.shortcut && (
        <span className="layer-connector-menu__item-shortcut">
          {item.shortcut}
        </span>
      )}
    </button>
  );

  const renderLayerActions = () => {
    const actionsDisabled = !selectionList.length || isApplyingLayerAction;
    const baseActions: MenuActionItem[] = [
      {
        key: "copy",
        label: "Copy",
        icon: <CopyOutlined />,
        onClick: handleCopyLayers,
        shortcut: formatPrimaryShortcut("C"),
        disabled: actionsDisabled,
      },
      {
        key: "duplicate",
        label: "Duplicate",
        icon: <SnippetsOutlined />,
        onClick: handleDuplicateLayers,
        disabled: actionsDisabled,
      },
      {
        key: "delete",
        label: "Delete",
        icon: <DeleteOutlined />,
        onClick: handleDeleteLayers,
        shortcut: "Delete",
        danger: true,
        disabled: actionsDisabled,
      },
    ];

    return (
      <>
        <div className="layer-connector-menu__section-title">Layer actions</div>
        {baseActions.map(renderMenuAction)}
        <div className="layer-connector-menu__section-title">Arrange</div>
        {LAYER_SORT_ACTIONS.map((item) =>
          renderMenuAction({
            key: item.action,
            label: item.label,
            icon: item.icon,
            shortcut: formatPrimaryShortcut(item.shortcutKey, {
              shift: item.shift,
            }),
            disabled: actionsDisabled,
            onClick: () => handleSortLayers(item.action),
          })
        )}
      </>
    );
  };

  const renderSection = (title: string, entries: ConnectorEntry[]) => (
    <>
      {title && (
        <div className="layer-connector-menu__section-title">{title}</div>
      )}
      {entries.map((entry) => {
        const available = getAvailability(entry);
        return (
          <button
            key={entry.key}
            className={`layer-connector-menu__item${
              available ? "" : " layer-connector-menu__item--disabled"
            }`}
            onClick={() => available && handleAddMod(entry.type)}
            type="button"
          >
            <span
              className={`layer-connector-menu__item-icon layer-connector-menu__item-icon--${entry.key}`}
            >
              <ConnectorIcon type={entry.type} />
            </span>
            <span>
              {entry.type === modTypes.asset && isVideoLayer
                ? "Video"
                : entry.label}
            </span>
          </button>
        );
      })}
    </>
  );

  const renderConnectorSections = () => (
    <>
      {renderSection("", PRIMARY_CONNECTORS)}
      {renderSection("Interactives", INTERACTIVE_CONNECTORS)}
    </>
  );

  return (
    <div
      className="layer-connector-menu"
      style={anchorStyle}
      ref={menuRef}
      role="menu"
    >
      {layerInspectorMode !== "design" && (
        <div className="layer-connector-menu__title">Create connector</div>
      )}
      {layerInspectorMode === "design" ? (
        <>
          {renderAnimationSection()}
          {renderLayerActions()}
        </>
      ) : (
        renderConnectorSections()
      )}
    </div>
  );
});
